import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Zap, Plug, Bot, KeyRound, Mail } from 'lucide-react';

const CONTACT_EMAIL = 'rochelle@fastreply247.com';
const CONTACT_SUBJECT = 'HighConnect – Access Request';

const FEATURES = [
  {
    icon: Plug,
    title: 'Plug into GHL',
    desc: 'Connects to your existing GoHighLevel account via webhook',
  },
  {
    icon: Bot,
    title: 'Choose your bot',
    desc: 'Lead Qualifier, Appointment Setter, or Outreach Bot',
  },
  {
    icon: KeyRound,
    title: 'Use your own AI key',
    desc: 'Bring your OpenAI key, keep full control of costs',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#141414] relative overflow-hidden">
      {/* Ambient glow */}
      <div
        className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, rgba(123,47,255,0.08) 0%, transparent 70%)' }}
      />

      {/* Nav */}
      <header className="relative z-10 max-w-[1100px] mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#7B2FFF] rounded-md flex items-center justify-center">
            <Zap className="w-4.5 h-4.5 text-white" />
          </div>
          <span
            className="text-xl font-bold tracking-tight text-[#EDEDF0]"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
            data-testid="landing-brand"
          >
            HighConnect
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="landing-login-nav"
            onClick={() => navigate('/login')}
            className="text-sm text-[#A1A1AA] hover:text-[#EDEDF0] transition-colors px-4 py-2 rounded-md hover:bg-[#ffffff06]"
          >
            Log in
          </button>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 max-w-[1100px] mx-auto px-6 pt-16 sm:pt-24 pb-20">
        <div className="max-w-[720px]">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#EDEDF0] leading-[1.1]"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
            data-testid="landing-headline"
          >
            AI-Powered Conversations for Your GHL Agency
          </h1>
          <p className="text-base sm:text-lg text-[#A1A1AA] mt-5 leading-relaxed max-w-[600px]">
            Connect your GoHighLevel CRM to OpenAI in minutes. Qualify leads, set appointments,
            and automate outreach — without touching a line of code.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-start gap-4 mt-10">
            <Button
              data-testid="landing-cta-button"
              asChild
              className="bg-[#7B2FFF] hover:bg-[#9353FF] text-white font-semibold rounded-md h-12 px-8 text-base transition-all duration-200 hover:shadow-[0_0_24px_rgba(123,47,255,0.3)] active:scale-[0.98]"
            >
              <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(CONTACT_SUBJECT)}`}>
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact for Access
                </span>
              </a>
            </Button>
            <button
              data-testid="landing-login-link"
              onClick={() => navigate('/login')}
              className="text-sm text-[#71717A] hover:text-[#EDEDF0] transition-colors py-3 sm:py-0 sm:self-center"
            >
              Already have an account? <span className="text-[#7B2FFF]">Log in</span>
            </button>
          </div>
        </div>

        {/* Feature tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-20" data-testid="landing-features">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="border border-[#27272A] rounded-lg bg-[#1A1A1A] p-6 transition-all duration-200 hover:border-[#7B2FFF]/30 hover:bg-[#1A1A1A]/80 group"
            >
              <div className="w-10 h-10 rounded-md bg-[#7B2FFF]/10 flex items-center justify-center mb-4 group-hover:bg-[#7B2FFF]/15 transition-colors">
                <Icon className="w-5 h-5 text-[#7B2FFF]" />
              </div>
              <h3 className="text-sm font-semibold text-[#EDEDF0] mb-1" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                {title}
              </h3>
              <p className="text-sm text-[#71717A] leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-24 pt-6 border-t border-[#27272A] flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-[#71717A]" data-testid="landing-footer">
          <span>© 2025 HighConnect</span>
          <span aria-hidden="true">·</span>
          <Link to="/privacy" className="hover:text-[#EDEDF0] transition-colors" data-testid="landing-footer-privacy">
            Privacy Policy
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms" className="hover:text-[#EDEDF0] transition-colors" data-testid="landing-footer-terms">
            Terms of Service
          </Link>
        </div>
      </main>
    </div>
  );
}
